/**
 * Layout for everything behind sign-in. Session gate → authz gate →
 * clinic chooser (ambiguity answered by the person) → shell.
 * Failure states are the designed ones, all fail-closed with a way out:
 * "couldn't check your access" (retry / sign out), suspended, no clinic
 * access, clinic temporarily inactive (ZOY-612), and — ZOY-610 — the
 * frozen tab whose clinic was switched elsewhere (reload / sign out). A
 * signed-out visit to an authenticated route remembers where it was going.
 */

import { useEffect, useRef } from 'react';
import { Navigate, Outlet, useRouterState } from '@tanstack/react-router';
import { useIntl } from 'react-intl';
import { useSession } from '@/core/auth/SessionProvider';
import { peekIntent, rememberIntent } from '@/core/auth/returnTo';
import { signedOutDestination } from '@/features/entry/entryIntent';
import { AuthzProvider, useAuthz } from '@/core/authz/AuthzProvider';
import { clinicIsActive } from '@/core/authz/api';
import { Shell } from './shell/Shell';
import { ClinicChooserPage } from '@/features/chooser/ClinicChooserPage';
import { Button, Notice, Skeleton } from '@/ui/kit';
import f from '@/ui/formkit.module.css';
import s from '@/features/access/access.module.css';

function CenterBox({ children }: { children: React.ReactNode }) {
  return (
    <div className={s.page}>
      <div
        style={{
          inlineSize: 'min(480px, 100%)',
          display: 'flex',
          flexDirection: 'column',
          gap: 14,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function BootSkeleton() {
  return (
    <CenterBox>
      <Skeleton height="20px" width="40%" />
      <Skeleton height="14px" />
      <Skeleton height="14px" width="80%" />
    </CenterBox>
  );
}

export function AuthzGate() {
  const intl = useIntl();
  const t = (id: string, v?: Record<string, string>) => intl.formatMessage({ id }, v);
  const authz = useAuthz();
  const { signOut } = useSession();

  // ZOY-610 cross-tab: this tab's clinic was switched away elsewhere. The
  // screen stays (unsaved text is not thrown away) but nothing goes out —
  // the HTTP layer refuses clinic calls — until the person reloads or leaves.
  const frozenDialog = authz.frozen && <FrozenDialog clinicId={authz.frozen.clinicId} />;
  const lostNotice = authz.lost && (
    <Notice kind="warn" role="alert" title={t('ctx.lostTitle') as string}>
      {t(authz.lost === 'CLINIC_INACTIVE' ? 'ctx.lostInactive' : 'ctx.lostBody')}
    </Notice>
  );

  switch (authz.state.status) {
    case 'loading':
      return <BootSkeleton />;
    case 'error':
      return (
        <CenterBox>
          <Notice kind="warn" title={t('error.authzDownTitle')} role="alert">
            {t('error.authzDownBody')}
          </Notice>
          {/* ZOY-306: no failure state may strand a signed-in person. */}
          <div style={{ display: 'flex', gap: 10 }}>
            <Button variant="brand" onClick={() => authz.refetch()}>
              {t('app.retry')}
            </Button>
            <Button variant="quiet" onClick={() => void signOut()}>
              {t('account.signOut')}
            </Button>
          </div>
        </CenterBox>
      );
    case 'suspended':
      return (
        <CenterBox>
          <Notice kind="err" title={t('error.suspendedTitle')} role="alert">
            {t('error.suspendedBody')}
          </Notice>
          <div>
            <Button variant="quiet" onClick={() => void signOut()}>
              {t('account.signOut')}
            </Button>
          </div>
        </CenterBox>
      );
    case 'inactive': {
      const others = authz.state.snapshot.memberships.filter(
        (m) => m.clinicId !== authz.clinicId && m.status === 'active' && clinicIsActive(m),
      );
      return (
        <CenterBox>
          {lostNotice}
          <Notice kind="warn" title={t('error.inactiveTitle') as string} role="alert">
            {t('error.inactiveBody')}
          </Notice>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
            {others.length > 0 && (
              <Button variant="brand" onClick={() => authz.clearSelection()}>
                {t('error.inactiveChoose')}
              </Button>
            )}
            <Button variant="quiet" onClick={() => authz.refetch()}>
              {t('app.retry')}
            </Button>
            <Button variant="quiet" onClick={() => void signOut()}>
              {t('account.signOut')}
            </Button>
          </div>
        </CenterBox>
      );
    }
    case 'chooseClinic':
      if (authz.state.snapshot.memberships.length === 0) {
        return (
          <CenterBox>
            {lostNotice}
            <Notice kind="err" title={t('error.noAccessTitle') as string} role="alert">
              {t('error.noAccessBody')}
            </Notice>
            <div style={{ display: 'flex', gap: 10 }}>
              <Button variant="brand" onClick={() => authz.refetch()}>
                {t('app.retry')}
              </Button>
              <Button variant="quiet" onClick={() => void signOut()}>
                {t('account.signOut')}
              </Button>
            </div>
          </CenterBox>
        );
      }
      return (
        <>
          {lostNotice && (
            <div className={s.page} style={{ paddingBlockEnd: 0 }}>
              <div style={{ inlineSize: 'min(860px, 100%)' }}>{lostNotice}</div>
            </div>
          )}
          <ClinicChooserPage memberships={authz.state.snapshot.memberships} />
          {frozenDialog}
        </>
      );
    case 'ready':
      return (
        <>
          <Shell />
          {frozenDialog}
        </>
      );
  }
}

/**
 * ZOY-610: a native modal (the ConfirmDialog primitive) over whatever the tab
 * was showing. It names the clinic the other tab moved to, says this tab has
 * stopped sending requests, and offers the only two honest exits.
 */
function FrozenDialog({ clinicId }: { clinicId: string | null }) {
  const intl = useIntl();
  const t = (id: string, v?: Record<string, string>) => intl.formatMessage({ id }, v);
  const authz = useAuthz();
  const { signOut } = useSession();
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const d = ref.current;
    if (d && !d.open) d.showModal();
  }, []);
  const name =
    (authz.state.status !== 'loading' && authz.state.status !== 'error'
      ? authz.state.snapshot.memberships.find((m) => m.clinicId === clinicId)?.displayName
      : null) ?? null;
  return (
    <dialog
      ref={ref}
      className={f.dialog}
      aria-label={t('ctx.changedTitle') as string}
      onCancel={(e) => e.preventDefault()}
    >
      <h2 className={f.dialogTitle}>{t('ctx.changedTitle')}</h2>
      <div className={f.dialogBody}>
        {clinicId === null
          ? t('ctx.changedToNone')
          : t('ctx.changedBody', { clinic: name ?? (t('chooser.unnamed') as string) })}
      </div>
      <div className={f.dialogActions}>
        <Button variant="quiet" type="button" onClick={() => void signOut()}>
          {t('account.signOut')}
        </Button>
        <Button variant="brand" type="button" onClick={() => window.location.reload()}>
          {t('ctx.reload')}
        </Button>
      </div>
    </dialog>
  );
}

/**
 * A signed-out visit to an authenticated route: remember where, then to
 * sign-in — or, when this tab arrived through a clinic's entry URL, back to
 * that URL (ZOY-614), which is what the clinic hands its staff.
 */
function BounceToSignIn() {
  const href = useRouterState({ select: (st) => st.location.href });
  useEffect(() => {
    rememberIntent(href);
  }, [href]);
  return <Navigate to={signedOutDestination()} />;
}

export function AuthedLayout() {
  const { session } = useSession();
  if (session.status === 'booting') return <BootSkeleton />;
  if (session.status === 'signedOut') return <BounceToSignIn />;
  return (
    <AuthzProvider uid={session.me.uid}>
      <AuthzGate />
    </AuthzProvider>
  );
}

/**
 * Public-route guard: an already-signed-in user goes on — to where they were
 * heading when they got bounced (their own intent only, ZOY-610), else their
 * default landing (/home: the Dashboard for whoever may open it, else Today).
 */
export function PublicOnly({ children }: { children: React.ReactNode }) {
  const { session } = useSession();
  if (session.status === 'booting') return <BootSkeleton />;
  if (session.status === 'signedIn') return <Navigate to={peekIntent(session.me.uid) ?? '/home'} />;
  return <>{children}</>;
}

export function RouteFallback() {
  return <Outlet />;
}
